import { MinsterNumberPickerComponent } from "../ui-components/minster-number-picker/minster-number-picker.component";
import { MinsterShowHideComponent } from "../ui-components/minster-show-hide/minster-show-hide.component";
import { MinsterCheckboxComponent } from "../ui-components/minster-checkbox/minster-checkbox.component";
import { MinsterSelectComponent } from "../ui-components/minster-select/minster-select.component";
import { ControlContainer, FormsModule, NgForm, NgModelGroup, Validators } from '@angular/forms';
import { MinsterCardComponent } from '../ui-components/minster-card/minster-card.component';
import { ScreenBaseComponent } from '../screen-base/screen-base.component';
import { Hospital, LookUp } from '../../models/look-up.model';
import { ClaimsService } from '../../services/claims.service';
import { StateService } from '../../services/state.service';
import { MatButtonModule } from '@angular/material/button';
import { ScreenType } from '../../models/enums.models';
import { Component, ViewChild } from '@angular/core';
import { Injury } from '../../models/claim.model';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { MinsterAutocompleteComponent } from '../ui-components/minster-autocomplete/minster-autocomplete.component';

@Component({
    selector: 'app-your-injuries-medical-attention',
    standalone: true,
    imports: [MinsterSelectComponent, MinsterNumberPickerComponent, MinsterShowHideComponent,
        MinsterCheckboxComponent, FormsModule, MinsterCardComponent, MatButtonModule, MinsterAutocompleteComponent],
    templateUrl: './your-injuries-medical-attention.component.html',
    styleUrl: './your-injuries-medical-attention.component.css',
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})

export class YourInjuriesMedicalAttentionComponent extends ScreenBaseComponent {
    @ViewChild(NgModelGroup) _formGroup: NgModelGroup | undefined;

    _injury: Injury;
    _hospitals: Hospital[];
    _doctorRecommendedTreatment: boolean | null = null;

    //Workaround to fix the hospital load/save on the empty form
    hospitalIdProxy: number | null = null;
    nightsDetainedProxy: number | null = 0;
    _readonly: boolean = false;
    
    constructor(stateService: StateService, router: Router, toastr: ToastrService, claimService: ClaimsService) {
        super(router, ScreenType.YourInjuriesMedicalAttention, toastr, stateService, claimService);

        this._readonly = stateService.formIsSubmitted;
        this._injury = stateService.claim!.injury!;
        this._hospitals = stateService.lookUps?.hospitals!;

        if (this._injury.injuryHospitals.length > 0) {
            this.hospitalIdProxy = this.getHospitalId(0);
            this.nightsDetainedProxy = this.getNightsDetained(0);
        }

        if (this._injury.doctorRecommendsFurtherTreatmentId > 0) {
            switch (this._injury.doctorRecommendsFurtherTreatmentId) {
                case 1:
                    this._doctorRecommendedTreatment = true;
                    break;
                case 2:
                    this._doctorRecommendedTreatment = false;
                    break;
            }
        }
    }

    override isScreenValid(): boolean {
        return this.stateService.getFormValidationErrorsExcludeRequired(this._formGroup!.control) == 0;
    }

    onHospitalSoughtChange(value: boolean): void {
        if (value) {
            if (this._injury.injuryHospitals.length === 0) {
                this._injury.injuryHospitals.push({
                    hospitalId: null,
                    injuryId: this._injury.injuryId,
                    nightsDetained: 0,
                    injuryHospitalId: 0
                });
            }
        }
        else {
            this._injury.injuryHospitals = [];
        }
    }

    onMedicalAttentionSoughtChange(value: boolean): void {
        if (!value) {
            this._injury.isGpSought = false;
            this._injury.isParamedicSought = false;
            this._injury.isHospitalSought = false;
            this._injury.isOtherMedicalAttentionSought = false;
            this._injury.doctorRecommendsFurtherTreatmentId = null;

            this.onHospitalSoughtChange(false);
        }

        this.updateValidators();
    }

    onHospitalUsedChange(value: boolean): void {
        if (value == false) {
            this._injury.injuryHospitals[0]!.hospitalId = null;
            this._injury.injuryHospitals[0]!.nightsDetained = 0
        }
    }

    getNightsDetained(index: number): number | null {
        return this._injury.injuryHospitals[index]?.nightsDetained || 0;
    }

    setNightsDetained(index: number, value: number): void {
        if (!this._injury.injuryHospitals[index]) {
            this._injury.injuryHospitals[index] = {
                hospitalId: null,
                injuryId: this._injury.injuryId,
                nightsDetained: value,
                injuryHospitalId: 0
            };
        }
        else {
            this._injury.injuryHospitals[index].nightsDetained = value;
        }
    }

    onNightsDetainedChange(value: number): void {
        this.nightsDetainedProxy = value;
        this.setNightsDetained(0, value);
    }

    override onNext(): void {
        if(this._readonly){
            super.onNext();
            return;
        }
        let hasErrors = false;
        this.updateValidators();

        switch (this._doctorRecommendedTreatment) {
            case true:
                this._injury.doctorRecommendsFurtherTreatmentId = 1;
                break;
            case false:
                this._injury.doctorRecommendsFurtherTreatmentId = 2;
                break;
            default:
                this._injury.doctorRecommendsFurtherTreatmentId = null;
                break;
        }


        if (this._formGroup && this._formGroup.invalid) {
            Object.keys(this._formGroup.control.controls).forEach((key) => {
                const control = this._formGroup!.control.get(key);
                if (control) {
                    const errors = control.errors;

                    if (errors) {
                        const hasOnlyRequiredError = Object.keys(errors).length === 1 && errors['required'];
                        const hospitalNameControl = this._formGroup!.control.get(this._fcn.InjuryMedicalAttention.HospitalName);

                        const isHospitalNameWithRequiredError = control === hospitalNameControl && errors['required'] && this._injury.isHospitalSought === true;
                        if (this.stateService.formIsSubmitted || !hasOnlyRequiredError || isHospitalNameWithRequiredError) {
                            hasErrors = true;
                        }
                    }

                    control.markAsTouched();
                    control.markAsDirty();
                    control.updateValueAndValidity();
                }
            });

            if (hasErrors) {
                this.toastr.warning('Please fix the form errors before proceeding.');
                this.stateService.enableSaving = false;
                return;
            }
        }
        
        if (this.stateService.formIsSubmitted && this._formGroup!.invalid) {
            this.toastr.warning('Please fix the required fields before proceeding.');
            this.stateService.enableSaving = false;
            return;
        }

        this.stateService.enableSaving = this.hasStateChanged();

        super.onNext();
    }

    getHospitalId(index: number): number | null {
        return this._injury.injuryHospitals[index]?.hospitalId || null;
    }

    setHospitalId(index: number, value: number): void {
        if (!this._injury.injuryHospitals[index]) {
            this._injury.injuryHospitals[index] = {
                hospitalId: value,
                injuryId: this._injury.injuryId,
                nightsDetained: 0,
                injuryHospitalId: 0
            };
        }
        else {
            this._injury.injuryHospitals[index].hospitalId = value;
        }
    }

    onHospitalIdChange(value: number): void {
        this.hospitalIdProxy = value;
        this.setHospitalId(0, value);
    }

    updateValidators(): void {
        const controls = [
            {formName: this._fcn.InjuryMedicalAttention.GpAttentionSought, makeRequired: this._injury.isMedicalAttentionSought },
            {formName: this._fcn.InjuryMedicalAttention.ParamedicAttentionSought, makeRequired: this._injury.isMedicalAttentionSought },
            {formName: this._fcn.InjuryMedicalAttention.HospitalAttentionSought, makeRequired: this._injury.isMedicalAttentionSought },
            {formName: this._fcn.InjuryMedicalAttention.OtherAttentionSought, makeRequired: this._injury.isMedicalAttentionSought },
            {formName: this._fcn.InjuryMedicalAttention.HospitalName, makeRequired: this._injury.isHospitalSought, markDirty: false }
        ];

        controls.forEach(({ formName, makeRequired, markDirty }) => {
            const control = this._formGroup?.control.get(formName);

            if (control) {
                if (makeRequired && !control.hasValidator(Validators.required)) {
                    control.setValidators([Validators.required]);
                }
                else if (!makeRequired && control.validator) {
                    control.clearValidators();
                }

                if (markDirty) control.markAsDirty();
                control.updateValueAndValidity();
            }
        });
    }
}
